(ns app.front.ui.options-panel
  "Global options panel component for displaying view specific actions."
  (:require [app.front.ui :as ui]
            [app.front.ui.panels :as panels]
            [app.front.ui.side-panel :as side-panel]
            [app.front.view.workspace.workspace-labels :as workspace-labels]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]))

(rf/reg-sub
  ::panel-open?
  :<- [::panels/open? :options]
  :-> boolean)

(rf/reg-event-fx ::toggle-open
  (fn [_ _]
    {:dispatch-n [[::panels/toggle :options]
                  [::workspace-labels/close-panel]]}))

(defn actions-title
  "Return translated '<ViewName> actions', or fallback to 'Actions'
   if the view-name translation is missing."
  [tr tr-quiet match]
  (let [view-name-key (-> match :data :name)
        view-name     (some-> view-name-key vector tr-quiet)]
    (if (and view-name (not= view-name (name view-name-key)))
      (tr [:options-panel/actions-title] [view-name])
      (tr [:options-panel/actions]))))

(defn- submenu-button
  "Button with a right chevron for items that open a submenu"
  [{:keys [label on-click disabled]}]
  [:button.w-full.bg-gray-200.rounded-md.p-2.border-0.appearance-none.text-ui-night-sky-100.flex.items-center.justify-between
   {:type     "button"
    :class    ["hover:bg-ui-blue-10" "focus:bg-ui-blue-10" "min-h-0" "text-base" "font-normal"
               "normal-case" "no-underline" "text-left"
               (when disabled "text-ui-night-sky-70 cursor-not-allowed")]
    :disabled disabled
    :on-click on-click}
   [:span label]
   (cc/icon {:icon i/chevron-right :description ""})])

(defn- item-node [{:keys [id label disabled on-click href file-picker-opts loading? loading-description has-submenu?]}]
  ^{:key id}
  (if has-submenu?
    [submenu-button
     {:label    label
      :disabled disabled
      :on-click (fn [& args]
                  (when (fn? on-click)
                    (apply on-click args)))}]
    [side-panel/button
     (util/mmap
       {:disabled? disabled
        :on-click  (fn [& args]
                     (>evt [::panels/toggle-mobile-menu-open false])
                     (when (fn? on-click)
                       (apply on-click args)))}
       href
       file-picker-opts
       loading?
       loading-description)
     label]))

(defn- section-node [idx {:keys [title items]}]
  ^{:key idx}
  [:div
   (when title [side-panel/title title])
   (into [:div.flex.flex-col.gap-2.mt-5]
         (map item-node items))])

(defn options-panel [tr]
  (let [panel-open?            (<sub [::panel-open?])
        match                  (<sub [:route/match])
        workspace-id           (-> match :parameters :path :workspace-id)
        view-sub               (some-> match :data :view-sub)
        tr-quiet               (<sub [:tr/quiet])
        view-data              (when view-sub (<sub view-sub))
        {:keys [options]}      view-data
        {:keys [items]}        options
        panel-visible?         (seq items)
        label-management-open? (<sub [::workspace-labels/panel-open?])
        add-groups-open?       (<sub [::workspace-labels/add-groups-open?])
        sections               (when (seq items)
                                 [{:title (actions-title tr tr-quiet match)
                                   :items (->> items (sort-by :label))}])
        panel-key              (cond
                                 label-management-open? (str "opts-labels-" workspace-id)
                                 sections               (str "opts-" (count sections))
                                 :else                  "opts-empty")]
    ^{:key panel-key}
    [side-panel/side-panel
     {:panel-open? (and panel-visible? panel-open?) :panel-id :options}
     (if label-management-open?
       ;; Show Label Management sub-panel
       [:div.flex.flex-col.h-full
        [ui/back-button {:label    (tr [:labels/title])
                         :on-click #(if add-groups-open?
                                      (>evt [::workspace-labels/set-add-groups-open false])
                                      (>evt [::workspace-labels/close-panel]))}]
        [workspace-labels/options-panel-content tr]]
       ;; Show normal options list
       (into [:<>]
             (map-indexed section-node (or sections []))))]))
